import {  t  } from "@/client/libs/dummy-i18n";
import { ArrowRightIcon, CheckIcon, SparkleIcon } from "@phosphor-icons/react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, RadioGroup, RadioGroupItem } from "@stackmorph/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useLocalStorage } from "usehooks-ts";

import { useUser } from "@/client/services/user";

const ROLES = [
  { id: "student", label: t`Student`, icon: "🎓" },
  { id: "professional", label: t`Professional`, icon: "💼" },
  { id: "manager", label: t`Manager`, icon: "👔" },
  { id: "freelancer", label: t`Freelancer`, icon: "💻" },
];

const GOALS = [
  { id: "job", label: t`Find a new job`, icon: "🚀" },
  { id: "internship", label: t`Land an internship`, icon: "🌱" },
  { id: "promotion", label: t`Get a promotion`, icon: "📈" },
  { id: "career-change", label: t`Switch careers`, icon: "🔄" },
];

export const OnboardingModal = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useLocalStorage("onboarding-seen", false);
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("");
  const [goal, setGoal] = useState("");

  // Only show if user is logged in and hasn't seen onboarding
  // In a real app, we might check a DB flag or creation date
  if (!user || isOpen) return null;

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      setIsOpen(true);
      navigate("/builder/new");
    }
  };

  return (
    <Dialog open={!isOpen} onOpenChange={(open) => !open && setIsOpen(true)}>
      <DialogContent className="sm:max-w-md">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <DialogHeader>
                <DialogTitle className="text-center text-2xl">
                  {t`Welcome to Stackmorph!`} <span className="inline-block animate-wave">👋</span>
                </DialogTitle>
                <DialogDescription className="text-center text-muted-foreground">
                  {t`Let's get to know you better so we can tailor your experience.`}
                </DialogDescription>
              </DialogHeader>

              <div className="py-6">
                <Label className="mb-4 block text-base font-medium">{t`Which describes you best?`}</Label>
                <RadioGroup value={role} className="grid grid-cols-2 gap-4" onValueChange={setRole}>
                  {ROLES.map((r) => (
                    <div key={r.id}>
                      <RadioGroupItem value={r.id} id={r.id} className="peer sr-only" />
                      <Label
                        htmlFor={r.id}
                        className="flex cursor-pointer flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 [&:has([data-state=checked])]:border-primary"
                      >
                        <span className="mb-2 text-2xl">{r.icon}</span>
                        {r.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <DialogHeader>
                <DialogTitle className="text-center text-2xl">{t`What is your main goal?`}</DialogTitle>
                <DialogDescription className="text-center text-muted-foreground">
                  {t`We'll recommend the best templates for you.`}
                </DialogDescription>
              </DialogHeader>

              <div className="py-6">
                <RadioGroup value={goal} className="grid grid-cols-1 gap-3" onValueChange={setGoal}>
                  {GOALS.map((g) => (
                    <div key={g.id}>
                      <RadioGroupItem value={g.id} id={g.id} className="peer sr-only" />
                      <Label
                        htmlFor={g.id}
                        className="flex cursor-pointer items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{g.icon}</span>
                          <span className="font-medium">{g.label}</span>
                        </div>
                        {goal === g.id && <CheckIcon className="text-primary" weight="bold" />}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <DialogHeader>
                <DialogTitle className="text-center text-2xl">{t`You're all set!`}</DialogTitle>
                <DialogDescription className="text-center text-muted-foreground">
                  {t`Ready to build your professional resume?`}
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="mb-6 rounded-full bg-primary/10 p-6">
                  <SparkleIcon size={48} className="text-primary" weight="fill" />
                </div>
                <p className="max-w-xs text-sm text-muted-foreground">
                  {t`Our AI-powered builder will guide you through each section to create a standout resume.`}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <DialogFooter>
          <Button
            className="w-full"
            size="lg"
            disabled={(step === 1 && !role) || (step === 2 && !goal)}
            onClick={handleNext}
          >
            {step === 3 ? t`Create My Resume` : t`Continue`}
            {step < 3 && <ArrowRightIcon className="ml-2" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
